import React, { useEffect, useRef, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useLocation } from 'react-router-dom';
import { Search, ChevronDown, Headphones, Sparkles } from 'lucide-react';

const FaqItem = ({ index, item, hash }) => {
  const [isOpen, setIsOpen] = useState(false);
  const itemRef = useRef(null);

  useEffect(() => {
    if (hash && item.link === hash && itemRef.current) {
      window.scrollTo({
        top: itemRef.current.offsetTop - 120,
        behavior: 'smooth',
      });
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  return (
    <div
      ref={itemRef}
      id={item.link.replace('#', '')}
      className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden scroll-mt-28"
    >
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5 text-left hover:bg-panda-tint/20 transition-colors"
      >
        <span className="flex items-center gap-3 sm:gap-4">
          <span
            className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full font-heading font-bold text-xs sm:text-sm flex items-center justify-center transition-colors ${
              isOpen
                ? 'bg-[#E53935] text-white'
                : 'bg-panda-tint text-[#E53935]'
            }`}
          >
            {index + 1}
          </span>
          <span className="font-heading font-bold text-[#202124] text-sm sm:text-base">
            {item.title}
          </span>
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180 text-[#E53935]' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1">
          <div className="ml-10 sm:ml-12 bg-panda-tint/20 rounded-xl px-4 py-3.5 text-sm text-gray-600 leading-relaxed">
            {ReactHtmlParser(item.content)}
          </div>
        </div>
      )}
    </div>
  );
};

const Faq = () => {
  const { hash } = useLocation();
  const [query, setQuery] = useState('');

  const data = [
    {
      title: 'How do I order food on behalf of my team?',
      link: '#order-for-team',
      content:
        'If you’re ordering food for your team, you can easily do this by creating a <strong>Catered Order</strong>.<br/><br/>' +
        'Catered Orders let you select items on behalf of your team and place the order in one simple flow. You can choose whether meals are <strong>individually packaged and labeled</strong> for each person, or <strong>served family-style</strong> in trays with labels.<br/><br/>' +
        'They’re perfect when your team is busy and you’d prefer to make the selections for everyone. Pick the restaurant, customize the menu, and complete the order — all in a few clicks.',
    },
    {
      title:
        'How do I create a corporate order so my team members can choose their own meals?',
      link: '#group-order',
      content:
        'If you want team members to pick their own meals, create a <strong>Corporate Order</strong>.<br/><br/>' +
        '<ul style="margin-left:1rem;list-style:disc;"><li>Set a budget per person</li><li>Choose delivery date and time</li><li>Invite team members to join</li></ul><br/>' +
        'Once invited, they’ll get an email link to join and reminders to submit choices before processing.<br/><br/>' +
        'Corporate Orders are automatically processed <strong>24–48 hours before delivery</strong>, so it’s best to create them <strong>4–5 days in advance</strong>. They’re ideal for teams with diverse dietary needs.',
    },
    {
      title: 'How do I add or remove items from a catered order?',
      link: '#remove-items-catered-order',
      content:
        '<ol style="margin-left:1rem;list-style:decimal;"><li>Scroll to the restaurant’s menu and click <strong>“Add Item.”</strong></li><li>Use the <strong>+ icon</strong> to adjust quantities.</li><li>To remove items, click the <strong>X</strong> beside the item in your cart.</li></ol>',
    },
    {
      title:
        "How can I edit or cancel a Catered Order once it's submitted?",
      link: '#editing-catered-order',
      content:
        'You can edit or cancel your Catered Order <strong>anytime before it’s processed</strong> (and within the vendor’s notice period).<br/><br/>' +
        '<ul style="margin-left:1rem;list-style:disc;"><li>Add or remove items</li><li>Change delivery details or contact info</li><li>Cancel the order (before vendor’s required notice)</li></ul><br/>' +
        'Example: If the vendor requires 24 hours’ notice and delivery is Wednesday at 11:30 a.m., you can edit/cancel until Tuesday at 11:30 a.m.<br/><br/>' +
        '<ol style="margin-left:1rem;list-style:decimal;"><li>Log in → Click your name → <strong>Orders</strong></li><li>Find the order → Click <strong>View Order</strong></li><li>Select <strong>Edit Order</strong> or <strong>Cancel Order</strong></li></ol>',
    },
    {
      title:
        "How can I edit or cancel a Corporate Order once it's submitted?",
      link: '#editing-group-order',
      content:
        'Corporate Orders can be edited or canceled <strong>until they are processed</strong> and within the vendor’s required notice.<br/><br/>' +
        '<ul style="margin-left:1rem;list-style:disc;"><li>Organizers can change delivery info or invite more members.</li><li>Team members can update or remove their meal selections.</li></ul><br/>' +
        'If the entire order is canceled, everyone and the vendor will be notified automatically.',
    },
    {
      title: 'When will I receive the receipt for my order?',
      link: '#automatic-receipts-for-orders',
      content:
        'Receipts are sent once your order is processed and payment is complete, usually <strong>24–48 hours before delivery</strong>.',
    },
    {
      title: 'Are receipts emailed to me?',
      link: '#emailed-receipts',
      content:
        'Yes. Receipts are <strong>emailed and texted</strong> to you automatically when your order is processed.',
    },
    {
      title:
        'What if I delete the receipt email — can I retrieve my receipt?',
      link: '#lost-receipts',
      content:
        'Yes. Sign in → Click your name → <strong>Orders</strong> → <strong>Past Orders</strong> → <strong>View Order</strong> to download or print your PDF receipt.',
    },
    {
      title:
        'Can I make changes after the vendor’s minimum notice or once processed?',
      link: '#editing-processed-order',
      content:
        'Unfortunately no. Once an order is processed, it’s finalized and cannot be changed or canceled. Make any edits <strong>before</strong> the vendor’s required notice period.',
    },
    {
      title: 'Who do I contact if the delivery is late?',
      link: '#late-deliveries',
      content:
        'You’ll receive a text message <strong>30 minutes before delivery</strong> with the vendor’s contact info. Reach out directly to confirm delivery status if needed.',
    },
  ];

  const normalized = query.trim().toLowerCase();
  const filtered = !normalized
    ? data
    : data.filter(
        item =>
          item.title.toLowerCase().includes(normalized) ||
          (item.content || '').toLowerCase().includes(normalized),
      );

  return (
    <main className="bg-[#FDF6EE] pt-[88px]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-2">
              Help Center
            </p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#202124] leading-tight">
              Frequently Asked
              <br />
              Questions
            </h1>
            <p className="mt-4 text-gray-500 text-sm sm:text-base max-w-xl">
              Find quick answers about ordering, group meals, receipts,
              delivery and more.
            </p>
          </div>
          <div className="lg:col-span-4 hidden lg:flex justify-end relative">
            <Sparkles className="absolute -top-2 right-16 w-6 h-6 text-[#E53935]" />
            <img
              src="/assets/img/panda/hero-food-bowl.png"
              alt=""
              className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
            />
          </div>
        </div>

        <div className="relative mt-8 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm text-[#202124] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E53935]/30 focus:border-[#E53935]"
            placeholder="Search FAQs..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search FAQs"
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200/80 p-10 text-center text-gray-500 text-sm">
            No FAQs match “{query}”. Try a different search term.
          </div>
        ) : (
          filtered.map((item, index) => (
            <FaqItem key={item.link} index={index} item={item} hash={hash} />
          ))
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-14">
        <div className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
          <div className="w-14 h-14 rounded-full bg-panda-tint flex items-center justify-center flex-shrink-0">
            <Headphones className="w-6 h-6 text-[#E53935]" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[11px] font-bold text-[#E53935] uppercase tracking-wider mb-1">
              Still need help?
            </p>
            <h2 className="font-heading font-bold text-lg sm:text-xl text-[#202124]">
              We&apos;re here for you.
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Our support team can set up your catered or corporate order for
              you.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a
              className="px-5 py-2.5 rounded-xl bg-[#E53935] text-white text-sm font-heading font-bold hover:bg-[#d32f2f] transition-colors"
              href="mailto:support@pandaonwheels.com"
            >
              Contact Support
            </a>
            <a
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-[#202124] text-sm font-heading font-bold hover:border-[#E53935] hover:text-[#E53935] transition-colors"
              href="/browse-restaurants"
            >
              Browse Restaurants
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Faq;
